import React from 'react';
import {Divider, Icon, List, ListItem} from '@mui/material';
import MuiTypography from '@new-components/atoms/MuiTypography';
import MuiLink from '@new-components/atoms/MuiLink';
import MuiBox from '@new-components/atoms/MuiBox';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Link from 'next/link';

type BasicNavItem = {icon: React.ReactNode; label: string; link: string};
type ExtendedNavItem = {
  label: string;
  subText?: string;
  icon?: React.ReactNode;
  items: Omit<BasicNavItem, 'icon'>[];
};
type MobNavMenuList = {
  basicNavItems: BasicNavItem[];
  destinations: ExtendedNavItem;
  extras: ExtendedNavItem;
  home: BasicNavItem;
  onClose: () => void;
};

const MobNavMenuList: React.FC<MobNavMenuList> = React.memo((props) => {
  return (
    <List
      sx={{
        //!important is added throughout this component to override the li styles in the reset file
        paddingTop: '80px !important',
        background: '#2D2D2D',
        paddingInline: '16px !important',
        minHeight: '100%',
        overflow: 'auto',
      }}>
      <ListItem
        sx={{
          padding: '0px !important',
          borderBottom: '1px solid #82807E1A',
        }}>
        <MuiLink
          component={Link}
          prefetch={false}
          sx={{
            textDecoration: 'none',
            display: 'flex',
            gap: '16px',
            alignItems: 'center',
            padding: '12px 0px !important',
            flex: '1 1 auto',
          }}
          href={props.home.link}>
          <Icon
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: '#FFFFFF1A',
              borderRadius: '8px',
              fontSize: '40px',
            }}>
            {props.home.icon}
          </Icon>
          <MuiTypography
            sx={{
              fontSize: '19px',
              fontWeight: 400,
              lineHeight: '23.75px',
              letterSpacing: '-0.4px',
              textAlign: 'left',
              textUnderlinePosition: 'from-font',
              textDecorationSkipInk: 'none',
              color: '#FFFFFF',
            }}>
            {props.home.label}
          </MuiTypography>
        </MuiLink>
      </ListItem>
      {[props.destinations].map(({label, items, icon}) => (
        <Accordion
          elevation={0}
          sx={{
            'backgroundColor': '#2d2d2d',
            'color': 'white',
            '& .MuiAccordionSummary-root': {
              padding: '0px',
              borderBottom: '1px solid #82807E1A',
            },
            '& .MuiAccordionSummary-expandIconWrapper': {
              color: 'gray',
            },
          }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <MuiBox
              sx={{
                display: 'flex',
                gap: '16px',
                alignItems: 'center',
              }}>
              {icon && (
                <Icon
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: '#FFFFFF1A',
                    borderRadius: '8px',
                    fontSize: '40px',
                  }}>
                  {icon}
                </Icon>
              )}
              <MuiBox>
                <MuiTypography
                  sx={{
                    fontSize: '19px',
                    fontWeight: 400,
                    lineHeight: '23.75px',
                    letterSpacing: '-0.4px',
                    textAlign: 'left',
                    textUnderlinePosition: 'from-font',
                    textDecorationSkipInk: 'none',
                    color: '#FFFFFF',
                  }}>
                  {label}
                </MuiTypography>
              </MuiBox>
            </MuiBox>
          </AccordionSummary>
          <AccordionDetails>
            <List sx={{marginBlock: '12px 0px', width: '100%'}}>
              {items.map(({label, link}, index) => (
                <ListItem
                  sx={{
                    padding: '0px !important',
                    borderTop: !index ? 'none' : '1px solid #82807E1A',
                    flex: '1 1 auto',
                  }}>
                  <MuiLink
                    component={Link}
                    prefetch={false}
                    sx={{
                      textDecoration: 'none',
                      fontSize: '17px',
                      fontWeight: 400,
                      lineHeight: '23.8px',
                      letterSpacing: '-0.4000000059604645px',
                      textAlign: 'left',
                      textUnderlinePosition: 'from-font',
                      textDecorationSkipInk: 'none',
                      color: '#FFFFFF',
                      padding: '12px 0px !important',
                    }}
                    href={link}>
                    {label}
                  </MuiLink>
                </ListItem>
              ))}
            </List>
          </AccordionDetails>
        </Accordion>
      ))}
      {props.basicNavItems.map(({icon, label, link}, index) => (
        <ListItem
          sx={{
            padding: '0px !important',
            borderBottom:
              index !== props.basicNavItems.length - 1
                ? '1px solid #82807E1A'
                : 'none',
          }}>
          <MuiLink
            component={Link}
            sx={{
              textDecoration: 'none',
              display: 'flex',
              gap: '16px',
              alignItems: 'center',
              padding: '12px 0px !important',
              flex: '1 1 auto',
            }}
            href={link}>
            <Icon
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: '#FFFFFF1A',
                borderRadius: '8px',
                fontSize: '40px',
              }}>
              {icon}
            </Icon>
            <MuiTypography
              sx={{
                fontSize: '19px',
                fontWeight: 400,
                lineHeight: '23.75px',
                letterSpacing: '-0.4px',
                textAlign: 'left',
                textUnderlinePosition: 'from-font',
                textDecorationSkipInk: 'none',
                color: '#FFFFFF',
              }}>
              {label}
            </MuiTypography>
          </MuiLink>
        </ListItem>
      ))}
      {[props.extras].map(({label, subText, items, icon}) => (
        <>
          <Divider
            orientation='horizontal'
            sx={{backgroundColor: '#FFFFFF40', marginBlock: '24px'}}
          />
          <ListItem
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              padding: '0px !important',
            }}>
            <MuiBox
              sx={{
                display: 'flex',
                gap: '16px',
                alignItems: 'center',
              }}>
              {icon && (
                <Icon
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: '#FFFFFF1A',
                    borderRadius: '8px',
                    fontSize: '40px',
                  }}>
                  {icon}
                </Icon>
              )}
              <MuiBox>
                <MuiTypography
                  sx={{
                    fontSize: '19px',
                    fontWeight: 400,
                    lineHeight: '23.75px',
                    letterSpacing: '-0.4px',
                    textAlign: 'left',
                    textUnderlinePosition: 'from-font',
                    textDecorationSkipInk: 'none',
                    color: '#FFFFFF',
                  }}>
                  {label}
                </MuiTypography>
                <MuiTypography
                  sx={{
                    fontSize: '13px',
                    fontWeight: 300,
                    lineHeight: '16.25px',
                    textAlign: 'left',
                    textUnderlinePosition: 'from-font',
                    textDecorationSkipInk: 'none',
                    color: '#FFFFFFBF',
                    marginTop: '4px',
                  }}>
                  {subText}
                </MuiTypography>
              </MuiBox>
            </MuiBox>
            <List sx={{marginBlock: '12px 0px', width: '100%'}}>
              {items.map(({label, link}) => (
                <ListItem
                  sx={{
                    padding: '0px !important',
                    borderTop: '1px solid #82807E1A',
                    flex: '1 1 auto',
                  }}>
                  <MuiLink
                    component={Link}
                    prefetch={false}
                    sx={{
                      textDecoration: 'none',
                      fontSize: '17px',
                      fontWeight: 400,
                      lineHeight: '23.8px',
                      letterSpacing: '-0.4000000059604645px',
                      textAlign: 'left',
                      textUnderlinePosition: 'from-font',
                      textDecorationSkipInk: 'none',
                      color: '#FFFFFF',
                      padding: '12px 0px !important',
                    }}
                    href={link}>
                    {label}
                  </MuiLink>
                </ListItem>
              ))}
            </List>
          </ListItem>
        </>
      ))}
    </List>
  );
});

export default MobNavMenuList;
export type {MobNavMenuList};
